// React.
import { useEffect, useState } from 'react';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';

// Components.
import SqlStatisticChart from '../../../component/instance/chart/SqlStatisticChart';
import StatementSqlStatisticsTable from '../../../component/instance/table/StatementSqlStatisticsTable';

// Helper.
import { fetchResults } from '../../../helpers/utils';
import SqlApi from '../../../api/instance/SqlApi';
import {OracleSqlHashTimeSlice} from "../types";
import { showError } from '../../../utils/showAlert';

// Context.
import { useTimeRange } from '../../../context/TimeRangeContext';
import { buildApiQuery } from '../../../helpers/timeRangeUtils';

export default function SqlStatistics (props: {instance: InstanceTarget}) {
    const { instance } = props;
    const timeRangeContext = useTimeRange();

    const [sqlStatistics, setSqlStatistics] = useState<OracleSqlHashTimeSlice[]> ([]);
    const [loading, setLoading] = useState<boolean> (true);
    const [error, setError] = useState<string | null> (null);
    const [layout, setLayout] = useState<number>(2); // 1, 2, or 3 columns

    useEffect (() =>
    {
        async function load ()
        {
            try
            {
                setLoading (true);
                const results: OracleSqlHashTimeSlice[][] = await fetchResults (
                    [
                        SqlApi.fetchStatisticTime(
                            buildApiQuery(timeRangeContext.timeRange),
                            instance.id,
                            instance.type
                        )
                    ]);

                if (active)
                {
                    setLoading (false);
                    setSqlStatistics(results[0]);
                }
            }
            catch (x: any)
            {
                console.log (x.message);
                const errorMessage = x.message || x.toString();
                setError (errorMessage);
                showError('Failed to load SQL statistics', errorMessage);
                setLoading (false);
                setSqlStatistics ([]);
            }

            return true;
        }

        let active: boolean = true;
        load ();
        return () =>
        {
            // Clean up afterwards to avoid race conditions.
            active = false;
        }
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, instance.id]);

    return (<>
        {/* Layout toggle UI using FontAwesome icons */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <div style={{ background: '#fff', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', padding: '2px 8px', marginRight: 32, display: 'flex', gap: 2 }}>
                <button onClick={() => setLayout(1)} style={{ marginRight: 2, background: layout === 1 ? '#e0e0e0' : 'white', padding: '4px 8px', border: 'none', borderRadius: 4, outline: 'none', cursor: 'pointer' }} title="1 column">
                    <i className="fal fa-list fa-lg" />
                </button>
                <button onClick={() => setLayout(2)} style={{ marginRight: 2, background: layout === 2 ? '#e0e0e0' : 'white', padding: '4px 8px', border: 'none', borderRadius: 4, outline: 'none', cursor: 'pointer' }} title="2 columns">
                    <i className="fal fa-th-large fa-lg" />
                </button>
                <button onClick={() => setLayout(3)} style={{ background: layout === 3 ? '#e0e0e0' : 'white', padding: '4px 8px', border: 'none', borderRadius: 4, outline: 'none', cursor: 'pointer' }} title="3 columns">
                    <i className="fal fa-th fa-lg" />
                </button>
            </div>
        </div>
        <div className={`row row-cols-${layout}`}>
            <div id="statements" className="tab-pane fade show active" role="tabpanel" aria-labelledby="statements-tab">
                <StatementSqlStatisticsTable instance={props.instance} />
                <SqlStatisticChart loading={loading} error={error}
                                   data={sqlStatistics} statistic="duration"
                                   title="Duration (ms)" />
                <SqlStatisticChart loading={loading} error={error}
                                   data={sqlStatistics} statistic="executions"
                                   title="Executions" />
                <SqlStatisticChart loading={loading} error={error}
                                   data={sqlStatistics} statistic="cpu_time_milliseconds"
                                   title="CPU time (ms)" />
                <SqlStatisticChart loading={loading} error={error}
                                   data={sqlStatistics} statistic="physical_reads"
                                   title="Physical reads" />
                <SqlStatisticChart loading={loading} error={error}
                                   data={sqlStatistics} statistic="logical_reads"
                                   title="Logical Reads" />
                <SqlStatisticChart loading={loading} error={error}
                                   data={sqlStatistics} statistic="logical_writes"
                                   title="Logical writes" />
            </div>
        </div>
    </>)
}
